import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { translateServerError } from '@/lib/serverErrors';
import { siteLanguage } from '@/lib/accountLanguage';
import { takeAuthRedirect, withRedirect } from '@/lib/authRedirect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GoogleCallback = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth, setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // The listing the traveller was booking before leaving for Google, if any.
    const redirect = takeAuthRedirect();

    const processOAuth = async () => {
      try {
        // Extract OAuth code and state from URL
        const queryParams = new URLSearchParams(location.search);
        const code = queryParams.get('code');
        const stateParam = queryParams.get('state');
        
        // Fallback: check for legacy Emergent session_id (backward compatibility)
        const hash = location.hash.startsWith('#') ? location.hash.substring(1) : location.hash;
        const hashParams = new URLSearchParams(hash);
        const sessionId = hashParams.get('session_id') || queryParams.get('session_id');

        let role = 'client';
        
        // Try to extract role from state (new OAuth flow)
        if (stateParam) {
          try {
            const state = JSON.parse(decodeURIComponent(stateParam));
            role = state.role || 'client';
          } catch (e) {
            console.warn('Failed to parse state:', e);
          }
        }
        
        // Fallback: extract role from hash/query (legacy Emergent flow)
        if (!stateParam) {
          role = hashParams.get('role') || queryParams.get('role') || 'client';
        }

        // New direct Google OAuth flow
        let response;
        if (code) {
          response = await axios.post(
            `${API}/auth/google/session`,
            { code, role, language: siteLanguage() },
            { withCredentials: true }
          );
        }
        // Legacy Emergent OAuth flow (backward compatibility)
        else if (sessionId) {
          response = await axios.post(
            `${API}/auth/google/session`,
            { session_id: sessionId, role, language: siteLanguage() },
            { withCredentials: true }
          );
        }
        // No valid OAuth params
        else {
          throw new Error('No OAuth code or session ID');
        }

        // The POST /api/auth/google/session returns user data and sets cookies
        const userData = response.data;
        
        // Clean URL before updating user state
        try {
          window.history.replaceState(
            null,
            '',
            window.location.pathname
          );
        } catch (_) {
          window.location.hash = '';
          window.location.search = '';
        }

        // Directly update the user state with the response data
        setUser(userData);
        
        // Back to the listing being booked, else to the dashboard
        // (ProtectedRoute will now see user as authenticated)
        navigate(redirect || '/dashboard', { replace: true });
      } catch (err) {
        console.error('Google auth error:', err);
        setError(translateServerError(err?.response?.data?.detail) || i18n.t('googleCallback.authFailed'));
        const loginPath = withRedirect('/login', redirect);
        setTimeout(() => navigate(`${loginPath}${loginPath.includes('?') ? '&' : '?'}error=auth_failed`, { replace: true }), 1500);
      }
    };

    processOAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="google-callback">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent mx-auto mb-4"></div>
        <p className="text-lg">
          {error ? t('googleCallback.error', { error }) : t('googleCallback.connecting')}
        </p>
      </div>
    </div>
  );
};
